import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 延迟焦化装置 P&ID 详细图：加热炉 + 两台焦炭塔（24h 交替）+ 主分馏 + 水力除焦 */
const media = {
  oil: { label: '渣油 / 蜡油 / 油品', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  gas: { label: '焦化油气 / 富气', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  water: { label: '高压除焦水 / 冷切水', light: '#1565c0', dark: '#64b5f6', width: 2.6 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  fuel: { label: '燃料气', light: '#c2185b', dark: '#f06292', width: 2, dash: '7 4' },
};

const devices = [
  { id: 'v101', kind: 'hDrum' as const, x: 170, y: 189, tag: 'V-101', name: '渣油缓冲罐' },
  { id: 'p101', kind: 'pump' as const, x: 340, y: 193, tag: 'P-101', name: '原料泵' },
  { id: 'e101', kind: 'exchanger' as const, x: 470, y: 188, tag: 'E-101', name: '原料换热' },
  { id: 'f101', kind: 'furnace' as const, x: 620, y: 146, tag: 'F-101', name: '焦化加热炉', note: '出口 500℃' },
  { id: 't101', kind: 'reactor' as const, x: 800, y: 128, tag: 'T-101', name: '焦炭塔A', note: '生焦 24h' },
  { id: 't102', kind: 'reactor' as const, x: 950, y: 128, tag: 'T-102', name: '焦炭塔B', note: '切换/除焦' },
  { id: 't103', kind: 'column' as const, x: 1130, y: 101, tag: 'T-103', name: '主分馏塔' },
  { id: 'p102', kind: 'pump' as const, x: 700, y: 460, tag: 'P-102', name: '高压除焦水泵' },
];

const ports: PidPort[] = [
  { id: 'p-feed', label: '减压/常压渣油', medium: 'oil', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-fg', label: '燃料气', medium: 'fuel', x: 540, y: 648, direction: 'in' as const },
  { id: 'p-water', label: '除焦水', medium: 'water', x: 540, y: 470, direction: 'in' as const },
  { id: 'p-gas', label: '富气 / 液化气', medium: 'gas', x: 1380, y: 52, direction: 'out' as const },
  { id: 'p-gasoline', label: '汽油', medium: 'oil', x: 1360, y: 224, direction: 'out' as const },
  { id: 'p-coke', label: '石油焦', medium: 'water', x: 660, y: 690, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-feed', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'oil' },
  { id: 's02', from: 'v101', fromSide: 'right', to: 'p101', toSide: 'left', medium: 'oil' },
  { id: 's03', from: 'p101', fromSide: 'right', to: 'e101', toSide: 'left', medium: 'oil' },
  { id: 's04', from: 'e101', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'oil' },
  // 加热炉 → 两台焦炭塔（24h 交替切换）
  { id: 's05', from: 'f101', fromSide: 'right', to: 't101', toSide: 'left', medium: 'oil' },
  { id: 's06', from: 'f101', fromSide: 'bottom', to: 't102', toSide: 'bottom', medium: 'oil' },
  // 焦炭塔顶油气 → 主分馏塔
  { id: 's07', from: 't101', fromSide: 'top', to: 't103', toSide: 'left', medium: 'gas' },
  { id: 's08', from: 't102', fromSide: 'top', to: 't103', toSide: 'left', medium: 'gas' },
  { id: 's09', from: 't103', fromSide: 'top', to: 'p-gas', toSide: 'left', medium: 'gas' },
  { id: 's10', from: 't103', fromSide: 'right', to: 'p-gasoline', toSide: 'left', medium: 'oil' },
  // 蜡油回炼：主分馏塔底 → 换热 → 原料（重组分走底部车道）
  { id: 's11', from: 't103', fromSide: 'bottom', to: 'e101', toSide: 'bottom', medium: 'oil' },
  // 水力除焦：高压水泵 → 焦炭塔顶
  { id: 's12', from: 'p-water', fromSide: 'right', to: 'p102', toSide: 'left', medium: 'water' },
  { id: 's13', from: 'p102', fromSide: 'right', to: 't101', toSide: 'top', medium: 'water' },
  { id: 's14', from: 'p-fg', fromSide: 'right', to: 'f101', toSide: 'bottom', medium: 'fuel' },
];

const DelayedCokingDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="延迟焦化装置详细流程"
    subtitle="渣油换热 → 加热炉（490~510℃ 快速加热）→ 两台焦炭塔 24h 交替生焦/除焦 → 塔顶油气入主分馏塔（富气/汽油/柴油/蜡油回炼）；高压水泵水力除焦（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default DelayedCokingDiagram;
